import { ExperimentTimelineCampaign, ExperimentTimelineSegment } from "@/http/api-types";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const CAMPAIGN_TIMELINE_DAY_WIDTH = 18;
export const CAMPAIGN_TIMELINE_MIN_WIDTH = 960;
const CAMPAIGN_TIMELINE_CHART_COLOR_COUNT = 26;

export type ExperimentTimelineScale = {
  start: Date;
  end: Date;
  startMs: number;
  endMs: number;
  durationMs: number;
  width: number;
};

export type ExperimentTimelineHeaderItem = {
  key: string;
  label: string;
  left: number;
  width: number;
};

export type ExperimentTimelineTick = {
  key: string;
  label: string;
  left: number;
};

export type ExperimentTimelineDayMarker = {
  key: string;
  label: string;
  left: number;
};

export type ExperimentTimelineFrame = {
  left: number;
  width: number;
};

export type ExperimentTimelineLaneFrame = {
  top: number;
  height: number;
};

export function buildExperimentTimelineScale(
  windowStart: string,
  windowEnd: string,
  dayWidth = CAMPAIGN_TIMELINE_DAY_WIDTH,
): ExperimentTimelineScale {
  const start = new Date(windowStart);
  const end = new Date(windowEnd);
  const startMs = start.getTime();
  const endMs = end.getTime();
  const durationMs = Math.max(1, endMs - startMs);
  const dayCount = Math.max(1, Math.ceil(durationMs / MS_PER_DAY));

  return {
    start,
    end,
    startMs,
    endMs,
    durationMs,
    width: Math.max(CAMPAIGN_TIMELINE_MIN_WIDTH, dayCount * dayWidth),
  };
}

export function getExperimentTimelineSegmentFrame(
  segment: Pick<ExperimentTimelineSegment, "start_at" | "end_at">,
  scale: ExperimentTimelineScale,
): ExperimentTimelineFrame {
  const segmentStart = Math.max(new Date(segment.start_at).getTime(), scale.startMs);
  const segmentEnd = Math.min(new Date(segment.end_at).getTime(), scale.endMs);
  const left = ((segmentStart - scale.startMs) / scale.durationMs) * scale.width;
  const width = Math.max(1, ((segmentEnd - segmentStart) / scale.durationMs) * scale.width);

  return { left, width };
}

export function getExperimentTimelineScrollLeftForDate(date: Date, scale: ExperimentTimelineScale): number {
  const left = ((date.getTime() - scale.startMs) / scale.durationMs) * scale.width;
  return Math.min(scale.width, Math.max(0, left));
}

export function getExperimentTimelineInitialScrollDate(now = new Date()): Date {
  const dayStart = utcDayStart(now);
  return new Date(dayStart.getTime() - 14 * MS_PER_DAY);
}

export function getExperimentTimelineLaneFrame(
  laneIndex: number,
  laneCount: number,
  trackHeight: number,
  laneGap = 3,
): ExperimentTimelineLaneFrame {
  const normalizedLaneCount = Math.max(1, laneCount);
  const availableHeight = Math.max(1, trackHeight - laneGap * (normalizedLaneCount - 1));
  const laneHeight = availableHeight / normalizedLaneCount;
  return {
    top: (laneHeight + laneGap) * Math.max(0, laneIndex),
    height: Math.max(8, laneHeight),
  };
}

export function buildExperimentTimelineMonthHeaders(scale: ExperimentTimelineScale): ExperimentTimelineHeaderItem[] {
  const headers: ExperimentTimelineHeaderItem[] = [];
  let cursor = utcMonthStart(scale.start);

  while (cursor.getTime() < scale.endMs) {
    const next = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 1));
    const startMs = Math.max(cursor.getTime(), scale.startMs);
    const endMs = Math.min(next.getTime(), scale.endMs);
    if (startMs < endMs) {
      headers.push({
        key: cursor.toISOString(),
        label: formatExperimentTimelineMonth(cursor),
        left: ((startMs - scale.startMs) / scale.durationMs) * scale.width,
        width: ((endMs - startMs) / scale.durationMs) * scale.width,
      });
    }
    cursor = next;
  }

  return headers;
}

export function buildExperimentTimelineWeekTicks(scale: ExperimentTimelineScale): ExperimentTimelineTick[] {
  const ticks: ExperimentTimelineTick[] = [];
  let cursor = utcWeekStart(scale.start);
  if (cursor.getTime() < scale.startMs) {
    cursor = new Date(cursor.getTime() + 7 * MS_PER_DAY);
  }

  while (cursor.getTime() < scale.endMs) {
    ticks.push({
      key: cursor.toISOString(),
      label: `${cursor.getUTCDate()}`,
      left: ((cursor.getTime() - scale.startMs) / scale.durationMs) * scale.width,
    });
    cursor = new Date(cursor.getTime() + 7 * MS_PER_DAY);
  }

  return ticks;
}

export function getExperimentTimelineUTCDayMarker(
  scale: ExperimentTimelineScale,
  now = new Date(),
): ExperimentTimelineDayMarker | null {
  const dayStart = utcDayStart(now);
  const dayStartMs = dayStart.getTime();
  if (dayStartMs < scale.startMs || dayStartMs >= scale.endMs) {
    return null;
  }

  return {
    key: dayStart.toISOString(),
    label: `${dayStart.getUTCDate()}`,
    left: ((dayStartMs - scale.startMs) / scale.durationMs) * scale.width,
  };
}

export function filterExperimentTimelineCampaigns(
  campaigns: ExperimentTimelineCampaign[],
  campaignType: "all" | "install" | "ctv_install",
  search: string,
): ExperimentTimelineCampaign[] {
  const normalizedSearch = search.trim().toLowerCase();

  return campaigns.filter(campaign => {
    if (campaignType !== "all" && campaign.campaign_type !== campaignType) {
      return false;
    }
    if (!normalizedSearch) {
      return true;
    }

    return [
      campaign.campaign_id.toString(),
      campaign.campaign_name,
      campaign.audience_id.toString(),
      campaign.audience_name,
      campaign.organization_id.toString(),
    ].some(value => value.toLowerCase().includes(normalizedSearch));
  });
}

export function getExperimentTimelineSegmentColor(
  segment: Pick<ExperimentTimelineSegment, "experiment_id" | "status">,
): string {
  if (segment.status === "concluded") {
    return "#9ca3af";
  }
  return getExperimentTimelineExperimentColor(segment.experiment_id);
}

export function getExperimentTimelineExperimentColor(experimentID: number): string {
  const normalizedID = Math.max(1, Math.abs(Math.trunc(experimentID)));
  const colorIndex = ((normalizedID - 1) % CAMPAIGN_TIMELINE_CHART_COLOR_COUNT) + 1;
  return `hsl(var(--chart-${colorIndex}))`;
}

export function formatExperimentTimelineMonth(date: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

export function formatExperimentTimelineDay(dateValue: string): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(new Date(dateValue));
}

export function formatExperimentTimelineExclusiveEndDay(dateValue: string): string {
  return formatExperimentTimelineDay(new Date(new Date(dateValue).getTime() - 1).toISOString());
}

function utcMonthStart(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
}

function utcDayStart(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

function utcWeekStart(date: Date): Date {
  const day = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  const cursor = new Date(day);
  const diffToMonday = (cursor.getUTCDay() + 6) % 7;
  cursor.setUTCDate(cursor.getUTCDate() - diffToMonday);
  return cursor;
}
